import groupBy from 'lodash/groupBy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@tymely/api';
import { IPrompt, IPromptCreate, IOrganization, LlmModelMetadata } from '@tymely/atoms';
import { AxiosError } from 'axios';
import { PROMPT_EDITOR_CHANNEL } from '@tymely/pages/PromptEditor/constants';

const LLM_MODEL_METADATA_LIST_QUERY_KEY = 'llm-model-metadata-list';
export const useLlmModelMetadatasQuery = () => {
    const api = useApi();
    return useQuery({
        queryKey: [LLM_MODEL_METADATA_LIST_QUERY_KEY],
        queryFn: () => api.get('/llm-model-metadatas/list', { params: { limit: 1000 } }) as Promise<LlmModelMetadata[]>,
    });
};

export const QUERY_PROMT = 'prompt';
export const QUERY_PROMTS = 'prompts';

export const usePromptQuery = ({ name }: { name: string }) => {
    const api = useApi();
    return useQuery({
        queryKey: [QUERY_PROMT, name],
        queryFn: () => api.get(PROMPT_EDITOR_CHANNEL, { params: { name } }) as Promise<IPrompt>,
    });
};

export const usePromptsQuery = (
    organizationId?: IOrganization['id'],
    selectedName?: string,
    include_deleted = true,
) => {
    const api = useApi();
    return useQuery({
        queryKey: [QUERY_PROMTS, selectedName, organizationId ?? undefined],
        queryFn: () =>
            api.get('/prompt-templates/search', {
                params: {
                    limit: 2000,
                    include_deleted,
                    organization_id: organizationId,
                    ignore_organization: !organizationId,
                    ...(selectedName ? { names: [selectedName] } : {}),
                    without_prompt: !selectedName && include_deleted,
                },
            }) as Promise<IPrompt[]>,
        initialData: [],
        select(data) {
            const groups = groupBy(data, 'name');
            return Object.keys(groups)
                .map((name) => {
                    const versions = groups[name].sort((a, b) => {
                        if (a.deleted_at && !b.deleted_at) {
                            return 1;
                        }
                        if (!a.deleted_at && b.deleted_at) {
                            return -1;
                        }
                        return new Date(b.created_date).valueOf() - new Date(a.created_date).valueOf();
                    });
                    const current = versions.find((v) => !v.deleted_at);
                    return { name, current, versions };
                })
                .sort((a, b) => {
                    const promptA = a.current;
                    const promptB = b.current;
                    const dateA = new Date(promptA?.created_date ?? 0);
                    const dateB = new Date(promptB?.created_date ?? 0);
                    return dateB.getTime() - dateA.getTime();
                });
        },
    });
};

export const useCreatePromptMutation = (organizationId?: IOrganization['id']) => {
    const api = useApi();
    const client = useQueryClient();
    return useMutation<IPrompt, AxiosError, IPromptCreate>({
        mutationFn: (prompt: IPromptCreate) => api.post(PROMPT_EDITOR_CHANNEL, prompt),
        onSuccess: (data: IPrompt) => {
            const updatePrompts = (prompts?: IPrompt[]) => {
                if (!prompts) {
                    return [data];
                }
                if (data.deleted_at) {
                    return prompts.concat(data);
                }
                return prompts
                    .map((prompt) => {
                        if (
                            prompt.name === data.name &&
                            prompt.organization_id === data.organization_id &&
                            !prompt.deleted_at
                        ) {
                            return { ...prompt, deleted_at: new Date().toISOString() };
                        }
                        return prompt;
                    })
                    .concat(data);
            };

            client.setQueryData([QUERY_PROMTS, data.name, organizationId ?? undefined], updatePrompts);
            client.setQueryData([QUERY_PROMTS], updatePrompts);
        },
        meta: {
            errorMessage: 'Failed to create a prompt.',
        },
    });
};

export const useUpdatePromptMutation = () => {
    const api = useApi();
    const client = useQueryClient();
    return useMutation<IPrompt, AxiosError, IPrompt>({
        mutationFn: async (prompt: IPrompt) => {
            const { id, created_date, ...rest } = prompt;
            await api.put(`${PROMPT_EDITOR_CHANNEL}/${id}`, rest);
            return prompt;
        },
        meta: {
            errorMessage: 'Failed to update prompt version',
        },
        onSuccess: (updatedPrompt) => {
            client.setQueryData([QUERY_PROMTS, updatedPrompt.organization_id], (prompts?: IPrompt[]) => {
                if (!prompts) {
                    return [];
                }
                return prompts.map((prompt) => {
                    if (prompt.id !== updatedPrompt.id) {
                        return prompt;
                    }
                    return updatedPrompt;
                });
            });
        },
    });
};

export const useSetLiveMutation = () => {
    const api = useApi();
    const client = useQueryClient();
    return useMutation({
        mutationFn: (prompt: IPrompt) =>
            api
                .put<IPrompt>(`${PROMPT_EDITOR_CHANNEL}/${prompt.id}/set-live`)
                .then(() => ({ ...prompt, deleted_at: undefined })),
        onSuccess: async (livePrompt) => {
            await Promise.all([
                client.invalidateQueries({ queryKey: [QUERY_PROMTS, livePrompt.name, undefined] }),
                client.invalidateQueries({ queryKey: [QUERY_PROMTS, undefined, undefined] }),
            ]);
        },
    });
};
